import type { ComponentProps, ReactNode, Ref } from 'react';
import { ArrowUpIcon, CornerDownLeftIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { AgentAttachmentsCopy } from './agent-attachments';

export interface AgentConversationPresentation {
  /** Welcome heading; the general landing heading is used when omitted. */
  heading?: string;
  introduction: string;
  guidance: string;
  starterQuestions: readonly string[];
  messagePlaceholder: string;
  /** File-attachment wording; the default wording is used when omitted. */
  attachments?: AgentAttachmentsCopy;
  headerActions?: ReactNode;
}

/** Solution Studio assistant wording shared by every Agent host. */
export const AGENT_LANDING_COPY = {
  heading: 'Ask a question, share an idea or work through a problem.',
  starters: 'Prefer some help putting it into words?',
  keyboardHint: 'Enter to send · Shift+Enter for a new line'
} as const;

/** The spacious landing shown before the first message: heading, description and starter pills. */
export function AgentWelcome({ heading = AGENT_LANDING_COPY.heading, introduction, guidance, questions, compact = false, disabled, onQuestion }: {
  heading?: string; introduction: string; guidance?: string; questions: readonly string[];
  compact?: boolean; disabled: boolean; onQuestion: (question: string) => void;
}) {
  return <section aria-label="Question guidance" className={cn('mx-auto flex w-full max-w-2xl flex-col items-center justify-center text-center', compact ? 'min-h-48 gap-2 py-4' : 'min-h-64 gap-3 py-10')}>
    <h3 className={cn('font-semibold tracking-tight text-foreground', compact ? 'text-lg' : 'text-2xl sm:text-3xl')}>{heading}</h3>
    <p className="mx-auto max-w-xl text-sm leading-6 text-muted-foreground">{introduction}</p>
    {guidance && <p className="mx-auto max-w-xl text-xs text-muted-foreground">{guidance}</p>}
    {questions.length > 0 && <div className="mx-auto mt-2 w-full max-w-xl">
      <p className="text-xs text-muted-foreground">{AGENT_LANDING_COPY.starters}</p>
      <div aria-label="Prompt suggestions" className={cn('mt-2 flex max-w-full gap-2', compact ? 'overflow-x-auto pb-1' : 'flex-wrap justify-center')}>
        {questions.map(question => <Button key={question} type="button" className={cn('h-10 rounded-full px-4', compact && 'shrink-0')} variant="outline" size="sm" disabled={disabled} onClick={() => onQuestion(question)}>{question}</Button>)}
      </div>
    </div>}
  </section>;
}

/**
 * One conversation turn in the reading layout: the person's words as a bold
 * line separated from the previous answer, and the Agent's answer as plain text.
 */
export function AgentMessageBubble({ role, children, turnId }: { role: 'user' | 'agent'; children: ReactNode; turnId?: string }) {
  return role === 'user'
    ? <div data-agent-turn-id={turnId} data-agent-turn="user" aria-label="You wrote"
        className="min-w-0 w-full border-t border-border/50 pt-6 text-base font-semibold leading-7 tracking-tight [overflow-wrap:anywhere] first:border-t-0 first:pt-0 sm:text-lg">{children}</div>
    : <div data-agent-turn-id={turnId} data-agent-turn="agent" className="min-w-0 w-full text-[15px] leading-7 [overflow-wrap:anywhere]">{children}</div>;
}

/** Presentation only: submission, access and recovery remain with each conversation owner. */
export function AgentComposerInput({ inputRef, sendDisabled, ...input }: Omit<ComponentProps<typeof Textarea>, 'ref'> & {
  inputRef?: Ref<HTMLTextAreaElement>; sendDisabled: boolean;
}) {
  const hintId = input.id ? `${input.id}-hint` : 'agent-composer-hint';
  const describedBy = [input['aria-describedby'], hintId].filter(Boolean).join(' ');
  return <div className="group relative border-b border-border/60 pb-1 transition-colors duration-150 focus-within:border-foreground/60">
    <span id={hintId} className="sr-only">{AGENT_LANDING_COPY.keyboardHint}</span>
    <div className="flex items-end gap-2">
      <Textarea ref={inputRef} rows={2} maxLength={4000} aria-label="Agent message" {...input} aria-describedby={describedBy}
        className={cn('min-h-[2.75rem] max-h-52 min-w-0 flex-1 resize-none rounded-none border-0 bg-transparent px-0 text-base leading-7 shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-transparent', input.className)}
        onKeyDown={input.onKeyDown ?? (event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
            event.preventDefault();
            const submit = event.currentTarget.form?.querySelector<HTMLButtonElement>('button[type="submit"]');
            if (submit && !submit.disabled) event.currentTarget.form?.requestSubmit(submit);
          }
        })} />
      <div className="flex shrink-0 items-center pb-0.5">
        <Button type="submit" size="sm" className="h-10 min-w-10 gap-1.5 rounded-full px-4" disabled={sendDisabled} aria-label="Send to Agent" title={AGENT_LANDING_COPY.keyboardHint}>
          <ArrowUpIcon className="size-4" aria-hidden />Send<CornerDownLeftIcon className="size-3.5 opacity-70" aria-hidden />
        </Button>
      </div>
    </div>
  </div>;
}
